import { Box, styled, type SxProps, type Theme, useTheme } from '@mui/system';
import { Button, Drawer, List, ListItem, ListItemText, useMediaQuery } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ROUTES } from '../utils/constant';
import { useCallback, useState } from 'react';
import { UID_KEY, EMAIL_KEY, CURRENT_USER_ROLE, DEPARTMENT_OWN, USER } from '../hooks/useLogin';
import { getCurrentUser } from 'src/utils/local';

import { useTranslation } from 'react-i18next';

export const NAVIGATOR_HEIGHT = 70;

const navigatorSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: {
    xs: 'space-between',
    lg: 'normal',
  },
  height: `${NAVIGATOR_HEIGHT}px`,
  width: '100%',
  fontSize: '18px',
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  padding: {
    xs: '0px 30px',
    md: '0px 60px',
    lg: '0px 150px',
  },
  '& .logout-btn': {
    position: 'absolute',
    right: '150px',
  },
  '& .MuiDrawer-paper': {
    padding: '2px',
  },
  '& .MuiListItem-root': {
    borderLeft: 'solid 2px #FFFFFF',
    '&.active-list-item': {
      borderColor: '#006EC2',
      color: '#006EC2',
      position: 'relative',
      left: '-2px',
    },
  },
};

const Tab = styled('div')({
  lineHeight: `${NAVIGATOR_HEIGHT}px`,
  height: `${NAVIGATOR_HEIGHT}px`,
  paddingBottom: '28px',
  marginLeft: '48px',
  '&.active': {
    borderBottom: 'solid 5px #8C8EBD',
    paddingBottom: '23px',
  },
  '&:hover': {
    cursor: 'pointer',
    opacity: 0.7,
  },
});

export default function Navigator () {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const routeMatch = {
    main: useRouteMatch(ROUTES.main),
    courseDetail: useRouteMatch(ROUTES.courseDetail),
    mission: useRouteMatch(ROUTES.mission),
    courses: useRouteMatch(ROUTES.courses),
    userList: useRouteMatch(ROUTES.userList),
    reportCourse: useRouteMatch(ROUTES.courseReportList),
    reportCourseDetail: useRouteMatch(ROUTES.courseReportDetail),
    reportMission: useRouteMatch(ROUTES.missionReportList),
    reportMissionDetail: useRouteMatch(ROUTES.missionReportDetail),
  };
  const [isDrawerVisible, toggleDrawer] = useState(false);
  const user = getCurrentUser();
  const isAdmin = user?.role === 'platform-admin' || user?.role === 'department-admin';

  const onLogout = useCallback(() => {
    sessionStorage.removeItem(UID_KEY);
    sessionStorage.removeItem('expired');
    sessionStorage.removeItem('ACCESS_TOKEN');
    sessionStorage.removeItem('REFRESH_TOKEN');
    sessionStorage.removeItem(CURRENT_USER_ROLE);
    sessionStorage.removeItem(DEPARTMENT_OWN);
    sessionStorage.removeItem(EMAIL_KEY);
    sessionStorage.removeItem(USER);
    history.push(ROUTES.login);
  }, [history]);

  return (
    <Box sx={navigatorSx}>
      <span style={{ color: '#19A277', fontWeight: 'bold' }}>{t('Online Training Class')}</span>
      {isMobile
        ? (
        <div>
          <Button
            variant="contained"
            onClick={() => { toggleDrawer(!isDrawerVisible); }}
            style={{ width: '35px', minWidth: 'auto', height: '35px', padding: 0, fontSize: '26px' }}
          >
            <i className="bi bi-list" />
          </Button>
          <Drawer open={isDrawerVisible} onClose={() => { toggleDrawer(false); }} anchor="right" disablePortal={true}>
            <List>
              <ListItem
                button
                className={(routeMatch.main || routeMatch.courseDetail) ? 'active-list-item' : ''}
                onClick={() => { history.push(ROUTES.main); }}
              >
                <ListItemText primary={t('Main')} />
              </ListItem>
              <ListItem
                button
                className={routeMatch.courses ? 'active-list-item' : ''}
                onClick={() => { history.push(ROUTES.courses); }}
              >
                <ListItemText primary={t('Course')} />
              </ListItem>
              <ListItem
                button
                className={routeMatch.mission ? 'active-list-item' : ''}
                onClick={() => { history.push(ROUTES.mission); }}
              >
                <ListItemText primary={t('Mission')} />
              </ListItem>
              {isAdmin && (
                <ListItem
                  button
                  className={routeMatch.userList ? 'active-list-item' : ''}
                  onClick={() => { history.push(ROUTES.userList); }}
                >
                  <ListItemText primary={t('User')} />
                </ListItem>
              )}
              {isAdmin && (
                <ListItem
                  button
                  className={
                    routeMatch.reportCourse ||
                    routeMatch.reportCourseDetail ||
                    routeMatch.reportMission ||
                    routeMatch.reportMissionDetail
                      ? 'active-list-item'
                      : ''
                  }
                  onClick={() => { history.push(ROUTES.courseReportList); }}
                >
                  <ListItemText primary={t('Report')} />
                </ListItem>
              )}
              <ListItem button onClick={onLogout}>
                <ListItemText primary={t('Logout')} />
              </ListItem>
            </List>
          </Drawer>
        </div>
          )
        : (
        <>
          <Tab
            className={routeMatch.main || routeMatch.courseDetail ? 'active' : ''}
            onClick={() => { history.push(ROUTES.main); }}
          >
            {t('Main')}
          </Tab>
          {isAdmin && (
            <Tab className={routeMatch.userList ? 'active' : ''} onClick={() => { history.push(ROUTES.userList); }}>
              {t('User')}
            </Tab>
          )}
          <Tab className={routeMatch.courses ? 'active' : ''} onClick={() => { history.push(ROUTES.courses); }}>
            {t('Course')}
          </Tab>
          <Tab className={routeMatch.mission ? 'active' : ''} onClick={() => { history.push(ROUTES.mission); }}>
            {t('Mission')}
          </Tab>
          {isAdmin && (
            <Tab
              className={
                routeMatch.reportCourse ||
                routeMatch.reportCourseDetail ||
                routeMatch.reportMission ||
                routeMatch.reportMissionDetail
                  ? 'active'
                  : ''
              }
              onClick={() => { history.push(ROUTES.courseReportList); }}
            >
              {t('Report')}
            </Tab>
          )}
          <Button
            variant="contained"
            className="logout-btn"
            startIcon={<i className="bi bi-box-arrow-left"></i>}
            onClick={onLogout}
          >
            {t('Logout')}
          </Button>
        </>
          )}
    </Box>
  );
}
